import check from '../libraries/check-types-ext';
import AppError from '../libraries/AppError';
import ProductMapper from './ProductMapper';
import Category from './Category';
import { ApmHelper, ApmSpanType } from '../libraries/ApmHelper';
import { TransientReleaseToggles } from '../transientReleaseToggles/TransientReleaseToggles';
import { pick, pickBy } from 'lodash';
import { extendProduct, sortProductsByComposite } from './ProductHelper';
const { LRUCache } = require('lru-cache');
const productCatalogCache = LRUCache
    ? new LRUCache({
        max: 150,
        ttl: 60 * 60 * 1000,
    })
    : undefined;
const CATEGORY_OBJ_TYPES = ['Category', 'DropDownMenuElement'];
export default class ProductCatalog {
    constructor(productCatalogData) {
        this.instancedFromCache = false;
        ApmHelper.apmDecorator(() => {
            check.object.orFail(productCatalogData);
            const enableLRUCache = this.shouldEnableCache(productCatalogData);
            if (enableLRUCache && productCatalogData.productCatalogCacheKey) {
                const cachedCatalog = productCatalogCache.get(productCatalogData.productCatalogCacheKey);
                if (cachedCatalog instanceof ProductCatalog) {
                    this.loadFromCache(cachedCatalog);
                    return;
                }
            }
            this.buildCatalogFromData(productCatalogData);
            if (enableLRUCache && productCatalogData.productCatalogCacheKey) {
                productCatalogCache.set(productCatalogData.productCatalogCacheKey, this);
            }
        }, {
            apmLabel: 'ProductCatalogConstructor',
            apmType: ApmSpanType.PURE_FUNCTION,
        })();
    }
    shouldEnableCache(productCatalogData) {
        if (!productCatalogCache) {
            return false;
        }
        if (!productCatalogData.products[0]) {
            return false;
        }
        const transientReleaseToggles = TransientReleaseToggles(process.env['NODE_ENV'], productCatalogData.products[0].tenantId);
        return transientReleaseToggles.enableProductCatalogObjectInMemoryCaching();
    }
    loadFromCache(cachedCatalog) {
        this.categoryMap = cachedCatalog.categoryMap;
        this.productMap = cachedCatalog.productMap;
        this.productNameMap = cachedCatalog.productNameMap;
        this.productSeoOptimizedNameToIdMap = cachedCatalog.productSeoOptimizedNameToIdMap;
        this.departments = cachedCatalog.departments;
        this.priceRules = cachedCatalog.priceRules;
        this.selectedShopId = cachedCatalog.selectedShopId;
        this.pickupTimeZone = cachedCatalog.pickupTimeZone;
        this.userTimeZone = cachedCatalog.userTimeZone;
        this.currentDate = cachedCatalog.currentDate;
        this.correspondingRootProducts = cachedCatalog.correspondingRootProducts;
        this.instancedFromCache = true;
    }
    buildCatalogFromData(productCatalogData) {
        check.array.orFail(productCatalogData.products, 'productCatalogData.products');
        check.array.orFail(productCatalogData.categories, 'productCatalogData.categories');
        const productMapper = new ProductMapper();
        const products = productCatalogData.products;
        const productsLength = products.length;
        for (let i = 0; i < productsLength; i++) {
            products[i] = extendProduct(products[i], productCatalogData);
        }
        products.sort(sortProductsByComposite);
        for (let i = 0; i < productsLength; i++) {
            productMapper.getProductObject(products[i]);
        }
        this.productMap = productMapper.productMap;
        this.productNameMap = productMapper.productNameMap;
        this.productSeoOptimizedNameToIdMap = productMapper.productSeoOptimizedNameToIdMap;
        this.correspondingRootProducts = productMapper.correspondingRootProducts;
        const categories = productCatalogData.categories;
        const categoryCount = categories.length;
        for (let i = 0; i < categoryCount; i++) {
            const categoryNode = categories[i];
            if (!categoryNode.disabled) {
                new Category(categoryNode, productMapper);
            }
        }
        this.categoryMap = productMapper.categoryMap;
        this.departments = (productCatalogData.departments || []).map((department) => {
            department.productIds = productCatalogData.products
                .filter((p) => p.departmentId === department.id)
                .map((p) => p.id);
            return department;
        });
        this.priceRules = productCatalogData.priceRules || [];
        this.selectedShopId = productCatalogData.selectedShopId;
        this.pickupTimeZone = productCatalogData.pickupTimeZone;
        this.userTimeZone = productCatalogData.userTimeZone;
        this.currentDate = productCatalogData.currentDate;
    }
    get categories() {
        return Object.values(this.categoryMap);
    }
    get products() {
        return Object.values(pickBy(this.productMap, (p) => p.isActive === true));
    }
    get productsWithDisabled() {
        return Object.values(this.productMap);
    }
    get mapper() {
        const productMapper = new ProductMapper();
        productMapper.productMap = this.productMap;
        productMapper.productNameMap = this.productNameMap;
        productMapper.productSeoOptimizedNameToIdMap = this.productSeoOptimizedNameToIdMap;
        productMapper.categoryMap = this.categoryMap;
        productMapper.correspondingRootProducts = this.correspondingRootProducts;
        return productMapper;
    }
    getAllProducts(includeDisabled = false) {
        return Object.values(includeDisabled ? this.productMap : pickBy(this.productMap, (p) => p.isActive === true));
    }
    getAllProductsByDepartment(departmentId, includeDisabled = false) {
        return this.getAllProducts(includeDisabled).filter((p) => p.departmentId === departmentId);
    }
    getProducts(productIds, includeDisabled = false) {
        return pick(includeDisabled ? this.productMap : pickBy(this.productMap, (p) => p.isActive === true), productIds);
    }
    getProduct(productId, includeDisabled = false) {
        const realProductId = (() => {
            if (productId && typeof productId === 'object') {
                check.string.orFail(productId.productId, 'productId');
                return productId.productId;
            }
            check.string.orFail(productId, 'productId');
            return productId;
        })();
        return ApmHelper.apmDecorator(() => {
            const product = this._extractProductFromProductMap(this.productMap, realProductId, includeDisabled);
            if (!product) {
                throw AppError.ProductIsUndefinedError().addData('productId', realProductId);
            }
            if (!product.id) {
                throw AppError.ProductReferenceError().addData('product', realProductId);
            }
            return product;
        }, {
            apmLabel: 'ProductMapperGetProduct',
            apmType: ApmSpanType.PURE_FUNCTION,
            loggingEnabled: false,
        })();
    }
    getProductByIdentifier(identifier, type = 'name', includeDisabled = false) {
        const name = identifier ? identifier.toLowerCase() : '';
        let productId;
        if (type === 'seo') {
            productId = this.productSeoOptimizedNameToIdMap[name];
        }
        else {
            productId = this.productNameMap[name];
        }
        if (!productId) {
            throw AppError.ProductIsUndefinedError().addData('name', name);
        }
        return this.getProduct(productId, includeDisabled);
    }
    getProductByName(name, includeDisabled = false) {
        return this.getProductByIdentifier(name, 'name', includeDisabled);
    }
    getProductBySeoOptimizedName(name, includeDisabled = false) {
        return this.getProductByIdentifier(name, 'seo', includeDisabled);
    }
    getProductBySeoNameOrName(name, includeDisabled = false) {
        try {
            return this.getProductBySeoOptimizedName(name, includeDisabled);
        }
        catch (e) {
            return this.getProductByName(name, includeDisabled);
        }
    }
    getProductName(productId, includeDisabled = false) {
        return this.getProduct(productId, includeDisabled).name;
    }
    getProductAsync(productId, includeDisabled = false) {
        return Promise.resolve(this.getProduct(productId, includeDisabled));
    }
    getProductByNameAsync(name, includeDisabled = false) {
        return Promise.resolve(this.getProductByName(name, includeDisabled));
    }
    getProductBySeoOptimizedNameAsync(productId, includeDisabled = false) {
        return Promise.resolve(this.getProductBySeoOptimizedName(productId, includeDisabled));
    }
    getProductBySeoNameOrNameAsync(productId, includeDisabled = false) {
        return Promise.resolve(this.getProductBySeoNameOrName(productId, includeDisabled));
    }
    getCategories() {
        return this.categories;
    }
    getCategory(categoryId) {
        const category = this.categoryMap[categoryId];
        if (!category) {
            throw AppError.CategoryIsUndefinedError().addData('categoryId', categoryId);
        }
        return category;
    }
    updateCategory() {
        throw AppError.createNewError('Product catalog object is read only, please recreate new one');
    }
    getHomePageCategory({ shopId = undefined, channel = undefined } = {}) {
        const homeCategories = this.getCategories().filter((category) => category.isHomeCategory === true);
        if (!homeCategories.length) {
            return null;
        }
        const filteredChannelHomeCategories = channel
            ? homeCategories.filter((category) => Array.isArray(category.isSpecificForChannels) && category.isSpecificForChannels.includes(channel))
            : [];
        const categoriesToFilter = filteredChannelHomeCategories.length > 0 ? filteredChannelHomeCategories : homeCategories;
        if (shopId !== undefined) {
            const shopSpecificCategories = categoriesToFilter.filter((category) => Array.isArray(category.isSpecificForShops) && category.isSpecificForShops.includes(shopId));
            if (shopSpecificCategories.length > 0) {
                return shopSpecificCategories[0];
            }
        }
        return filteredChannelHomeCategories[0] || homeCategories[0];
    }
    getDepartments() {
        return this.departments;
    }
    getDepartmentsWithCallback(callback) {
        callback(this.departments);
    }
    getDepartmentsAsync() {
        return Promise.resolve(this.getDepartments());
    }
    getDepartment(departmentId) {
        return this.departments.find((d) => d.id === departmentId);
    }
    getPickupTimeZone() {
        return this.pickupTimeZone;
    }
    getCategoryFromProduct(product) {
        return this.getCategories()
            .filter((cat) => CATEGORY_OBJ_TYPES.includes(cat.objType))
            .find((category) => this.categoryContainsProduct(category, product.id));
    }
    categoryContainsProduct(category, productId) {
        return category.elements.some((element) => {
            if ('items' in element && Array.isArray(element.items)) {
                return element.items.some((item) => {
                    if (!item || typeof item !== 'object')
                        return false;
                    if ('id' in item && item.id) {
                        return productId === item.id;
                    }
                    return false;
                });
            }
            if ('props' in element && element.props && Array.isArray(element.props.products)) {
                return element.props.products.includes(productId);
            }
            return false;
        });
    }
    getAllCategoriesOfProduct(productId) {
        return this.getCategories()
            .filter((cat) => CATEGORY_OBJ_TYPES.includes(cat.objType))
            .filter((category) => this.categoryContainsProduct(category, productId));
    }
    isInstancedFromCache() {
        return this.instancedFromCache;
    }
    _extractProductFromProductMap(productMap, productId, includeDisabled) {
        const product = productMap[productId];
        if (includeDisabled) {
            return product;
        }
        if (product && product.isActive) {
            return product;
        }
        return null;
    }
}
